import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { translate } from 'utils/i18nTranslate';

const APP_NAME_KEY = 'components.pageTitle.appName';

const ROUTE_TITLE_KEYS: Record<string, string> = {
  '/': 'components.pageTitle.routes.home.title',
  '/tax-calculator': 'components.pageTitle.routes.taxCalculator.title',
  '/dashboard': 'components.pageTitle.routes.dashboard.title',
  '/health-check': 'components.pageTitle.routes.healthCheck.title',
  '/super-dashboard': 'components.pageTitle.routes.superDashboard.title',
  '/super-dashboard/health-checks':
    'components.pageTitle.routes.superDashboardHealthChecks.title',
  '/super-dashboard/tax-reminders':
    'components.pageTitle.routes.superDashboardTaxReminders.title',
  '/super-dashboard/upload-entries':
    'components.pageTitle.routes.superDashboardUploadEntries.title',
  '/accounts-payable/bills/new':
    'components.pageTitle.routes.billsLedgerNew.title',
  '/ppe/new': 'components.pageTitle.routes.equipmentNew.title',
  '/journals': 'components.pageTitle.routes.journals.title',
  '/journals/customers': 'components.pageTitle.routes.customers.title',
  '/journals/customers/email':
    'components.pageTitle.routes.customerEmail.title',
  '/journals/customers/email/recent-sends':
    'components.pageTitle.routes.customerEmailRecentSends.title',
  '/journals/customers/bill-invoice':
    'components.pageTitle.routes.billInvoiceSettings.title',
  '/journals/suppliers': 'components.pageTitle.routes.suppliers.title',
  '/transactions': 'components.pageTitle.routes.transactions.title',
  '/transactions/add': 'components.pageTitle.routes.transactionsAdd.title',
  '/cash-receipts-journal':
    'components.pageTitle.routes.cashReceiptsJournal.title',
  '/sales': 'components.pageTitle.routes.cashReceiptsJournal.title',
  '/cash-disbursement-journal':
    'components.pageTitle.routes.cashDisbursementJournal.title',
  '/expenses': 'components.pageTitle.routes.cashDisbursementJournal.title',
  '/reports': 'components.pageTitle.routes.reports.title',
  '/reports/income-statement':
    'components.pageTitle.routes.incomeStatement.title',
  '/reports/balance-sheet': 'components.pageTitle.routes.balanceSheet.title',
  '/reports/cash-flow-statement':
    'components.pageTitle.routes.cashFlowStatement.title',
  '/reports/statement-of-changes-in-equity':
    'components.pageTitle.routes.statementOfChangesInEquity.title',
  '/reports/general-ledger': 'components.pageTitle.routes.generalLedger.title',
  '/reports/trial-balance': 'components.pageTitle.routes.trialBalance.title',
  '/reports/general-journal':
    'components.pageTitle.routes.generalJournal.title',
  '/reports/financial-statement':
    'components.pageTitle.routes.financialStatementGeneration.title',
  '/reports/cash-receipts-journal':
    'components.pageTitle.routes.cashReceiptsJournalReport.title',
  '/reports/cash-disbursement-journal':
    'components.pageTitle.routes.cashDisbursementJournalReport.title',
  '/reports/inventory': 'components.pageTitle.routes.inventoryReport.title',
  '/inventory': 'components.pageTitle.routes.inventory.title',
  '/inventory/add': 'components.pageTitle.routes.inventoryAdd.title',
  '/inventory/sources': 'components.pageTitle.routes.inventorySources.title',
  '/companies': 'components.pageTitle.routes.companies.title',
  '/user/onboarding': 'pages.user.onboarding.pageTitle',
  '/notifications': 'pages.notifications.pageTitle',
  '/tax-compliance': 'components.pageTitle.routes.taxCompliance.title',
  '/business-calendar': 'components.pageTitle.routes.businessCalendar.title',
  '/business-calendar/action-items':
    'components.pageTitle.routes.businessCalendarActionItems.title',
  '/ppe': 'components.pageTitle.routes.equipment.title',
  '/ppe/depreciation-report':
    'components.pageTitle.routes.equipmentDepreciationReport.title',
  '/hris': 'components.pageTitle.routes.hris.title',
  '/hris/employees': 'components.pageTitle.routes.hrisEmployees.title',
  '/hris/employees/new': 'components.pageTitle.routes.hrisEmployeeCreate.title',
  '/hris/employees/roles': 'components.pageTitle.routes.hrisRoleManager.title',
  '/hris/attendance': 'components.pageTitle.routes.hrisAttendance.title',
  '/hris/leave': 'components.pageTitle.routes.hrisLeave.title',
  '/hris/payroll': 'components.pageTitle.routes.hrisPayroll.title',
  '/hris/payroll/runs': 'components.pageTitle.routes.hrisPayrollRun.title',
  '/hris/reports': 'components.pageTitle.routes.hrisReports.title',
  '/hris/settings': 'components.pageTitle.routes.hrisSettings.title',
  '/hris/me': 'components.pageTitle.routes.hrisSelfService.title',
  '/portal': 'components.pageTitle.routes.employeePortal.title',
  '/portal/attendance':
    'components.pageTitle.routes.employeePortalAttendance.title',
  '/portal/leave': 'components.pageTitle.routes.employeePortalLeave.title',
  '/portal/payslips':
    'components.pageTitle.routes.employeePortalPayslips.title',
  '/portal/profile': 'components.pageTitle.routes.employeePortalProfile.title',
  '/hris/guide': 'components.pageTitle.routes.hrisGuide.title',
  '/reserve': 'components.pageTitle.routes.reserve.title',
  '/reserve/places/new': 'components.pageTitle.routes.reservePlacesNew.title',
  '/reserve/reservations':
    'components.pageTitle.routes.reserveReservations.title',
  '/reserve/email': 'components.pageTitle.routes.reserveEmail.title',
  '/reserve/payment': 'components.pageTitle.routes.reservePayment.title',
  '/help/equipment': 'components.pageTitle.routes.equipmentHelp.title',
  '/help/pos-inventory': 'components.pageTitle.routes.posInventoryHelp.title',
  '/downloadables': 'components.pageTitle.routes.downloadables.title',
  '/settings/general': 'components.pageTitle.routes.settingsGeneral.title',
  '/settings/notifications':
    'components.pageTitle.routes.settingsNotifications.title',
  '/settings/change-password':
    'components.pageTitle.routes.settingsChangePassword.title',
  '/settings/company-settings':
    'components.pageTitle.routes.settingsCompany.title',
  '/settings/company-details':
    'components.pageTitle.routes.settingsCompanyDetails.title',
  '/settings/company-details/edit':
    'components.pageTitle.routes.settingsCompanyDetailsEdit.title',
  '/settings/profile': 'components.pageTitle.routes.settingsProfile.title',
  '/super-admin/payment-methods':
    'components.pageTitle.routes.superAdminPaymentMethods.title',
  '/super-admin/paymongo-connection':
    'components.pageTitle.routes.superAdminPaymongoConnection.title',
  '/super-admin/subscription-plans':
    'components.pageTitle.routes.superAdminSubscriptionPlans.title',
  '/super-admin/openai-usage':
    'components.pageTitle.routes.superAdminOpenAiUsage.title',
  '/user/profile': 'components.pageTitle.routes.userProfile.title',
  '/user/change-password':
    'components.pageTitle.routes.userChangePassword.title',
};

function getHubSectionDescriptionKey(pathname: string): string {
  if (pathname === '/') {
    return 'components.pageTitle.metaDescription.home';
  }
  if (pathname.startsWith('/tax-calculator')) {
    return 'components.pageTitle.metaDescription.taxCalculator';
  }
  if (pathname.startsWith('/dashboard')) {
    return 'components.pageTitle.metaDescription.dashboard';
  }
  if (pathname.startsWith('/categories')) {
    return 'components.pageTitle.metaDescription.categories';
  }
  if (pathname.startsWith('/journals')) {
    return 'components.pageTitle.metaDescription.journals';
  }
  if (
    pathname.startsWith('/sales') ||
    pathname.startsWith('/cash-receipts-journal')
  ) {
    return 'components.pageTitle.metaDescription.cashReceiptsJournal';
  }
  if (
    pathname.startsWith('/expenses') ||
    pathname.startsWith('/cash-disbursement-journal')
  ) {
    return 'components.pageTitle.metaDescription.cashDisbursementJournal';
  }
  if (pathname.startsWith('/transactions')) {
    return 'components.pageTitle.metaDescription.transactions';
  }
  if (pathname.startsWith('/inventory')) {
    return 'components.pageTitle.metaDescription.inventory';
  }
  if (pathname.startsWith('/reports')) {
    return 'components.pageTitle.metaDescription.reports';
  }
  if (pathname.startsWith('/tax-compliance')) {
    return 'components.pageTitle.metaDescription.taxCompliance';
  }
  if (pathname.startsWith('/business-calendar')) {
    return 'components.pageTitle.metaDescription.businessCalendar';
  }
  if (pathname.startsWith('/ppe') || pathname.startsWith('/equipment')) {
    return 'components.pageTitle.metaDescription.equipment';
  }
  if (pathname.startsWith('/hris')) {
    return 'components.pageTitle.metaDescription.hris';
  }
  if (pathname.startsWith('/reserve')) {
    return 'components.pageTitle.metaDescription.reserve';
  }
  if (pathname === '/help/equipment') {
    return 'components.pageTitle.metaDescription.equipment';
  }
  if (pathname === '/help/pos-inventory') {
    return 'components.pageTitle.metaDescription.posInventory';
  }
  if (
    pathname.startsWith('/user/onboarding') ||
    pathname.startsWith('/user/profile') ||
    pathname.startsWith('/user/change-password') ||
    pathname.startsWith('/settings/change-password')
  ) {
    return 'components.pageTitle.metaDescription.userProfile';
  }
  if (pathname.startsWith('/company')) {
    return 'components.pageTitle.metaDescription.company';
  }
  return 'components.pageTitle.metaDescription.default';
}

function getTitleKeyFromPath(pathname: string): string {
  if (ROUTE_TITLE_KEYS[pathname]) {
    return ROUTE_TITLE_KEYS[pathname];
  }
  if (pathname.startsWith('/transactions/') && !pathname.includes('/add')) {
    if (pathname.includes('/edit')) {
      return 'components.pageTitle.dynamic.transactionsEdit.title';
    }
    return 'components.pageTitle.dynamic.transactionsView.title';
  }
  if (pathname.startsWith('/notifications/') && pathname !== '/notifications') {
    return 'pages.notifications.detail.pageTitle';
  }
  if (pathname.startsWith('/cash-receipts-journal/')) {
    if (pathname.endsWith('/new')) {
      return 'components.pageTitle.dynamic.cashReceiptsJournalNew.title';
    }
    if (pathname.includes('/edit')) {
      return 'components.pageTitle.dynamic.cashReceiptsJournalEdit.title';
    }
    return 'components.pageTitle.dynamic.cashReceiptsJournalView.title';
  }
  if (
    pathname.startsWith('/expenses/') ||
    pathname.startsWith('/cash-disbursement-journal/')
  ) {
    if (pathname.endsWith('/new')) {
      return 'components.pageTitle.dynamic.cashDisbursementJournalNew.title';
    }
    if (pathname.includes('/edit')) {
      return 'components.pageTitle.dynamic.cashDisbursementJournalEdit.title';
    }
    return 'components.pageTitle.dynamic.cashDisbursementJournalView.title';
  }
  if (
    pathname.startsWith('/inventory/') &&
    pathname !== '/inventory/add' &&
    pathname !== '/inventory/sources'
  ) {
    return 'components.pageTitle.dynamic.productInventory.title';
  }
  if (pathname.startsWith('/ppe/') && pathname !== '/ppe/depreciation-report') {
    if (pathname.endsWith('/edit')) {
      return 'components.pageTitle.dynamic.equipmentEdit.title';
    }
    return 'components.pageTitle.dynamic.equipmentView.title';
  }
  if (
    pathname.startsWith('/hris/payroll/runs/') &&
    pathname !== '/hris/payroll/runs'
  ) {
    return 'components.pageTitle.routes.hrisPayrollRun.title';
  }
  if (
    pathname.startsWith('/hris/employees/') &&
    pathname !== '/hris/employees' &&
    pathname !== '/hris/employees/new' &&
    pathname !== '/hris/employees/roles'
  ) {
    return 'components.pageTitle.dynamic.hrisEmployee.title';
  }
  if (pathname.startsWith('/reserve/places/')) {
    if (pathname.endsWith('/blocks')) {
      return 'components.pageTitle.dynamic.reservePlaceBlocks.title';
    }
    if (pathname !== '/reserve/places/new') {
      return 'components.pageTitle.dynamic.reservePlaceEdit.title';
    }
  }
  return 'components.pageTitle.fallback.appName';
}

function getTitleFromPath(pathname: string): string {
  const key = getTitleKeyFromPath(pathname);
  if (key === 'components.pageTitle.fallback.appName') {
    const segments = pathname.split('/').filter(Boolean);
    if (segments.length > 0) {
      const lastSegment = segments[segments.length - 1];
      return (
        lastSegment.charAt(0).toUpperCase() +
        lastSegment.slice(1).replace(/-/g, ' ')
      );
    }
  }
  return translate(key);
}

function PageTitle() {
  const location = useLocation();
  const { i18n } = useTranslation();

  useEffect(() => {
    if (typeof document === 'undefined') return;

    const appName = translate(APP_NAME_KEY);
    const title = getTitleFromPath(location.pathname);
    const fullTitle = title ? `${title} | ${appName}` : appName;
    const description = translate(
      getHubSectionDescriptionKey(location.pathname),
    );
    const href = window.location.href;
    const defaultOgImage = `${import.meta.env.BASE_URL}abrafi-og.png`;
    let ogImageUrl = defaultOgImage;
    try {
      ogImageUrl = new URL(defaultOgImage, window.location.origin).href;
    } catch {
      // Keep defaultOgImage as-is if URL resolution fails.
    }

    document.title = fullTitle;

    const upsertMeta = ({
      name,
      property,
      content,
    }: {
      name?: string;
      property?: string;
      content: string;
    }) => {
      const selector = property
        ? `meta[property="${property}"]`
        : `meta[name="${name}"]`;
      const existing = document.head.querySelector(selector);

      if (existing instanceof HTMLMetaElement) {
        existing.content = content;
        return;
      }

      const meta = document.createElement('meta');
      if (property) meta.setAttribute('property', property);
      if (name) meta.setAttribute('name', name);
      meta.setAttribute('content', content);
      document.head.appendChild(meta);
    };

    upsertMeta({ name: 'description', content: description });
    upsertMeta({ property: 'og:title', content: fullTitle });
    upsertMeta({ property: 'og:description', content: description });
    upsertMeta({ property: 'og:site_name', content: appName });
    upsertMeta({ property: 'og:image', content: ogImageUrl });
    upsertMeta({ property: 'og:url', content: href });
    upsertMeta({ name: 'twitter:title', content: fullTitle });
    upsertMeta({ name: 'twitter:description', content: description });
    upsertMeta({ name: 'twitter:image', content: ogImageUrl });

    const canonicalLink = document.getElementById(
      'canonical',
    ) as HTMLLinkElement | null;
    if (canonicalLink) canonicalLink.href = href;

    const ldJsonScript = document.getElementById(
      'abrafi-ld-json',
    ) as HTMLScriptElement | null;
    if (ldJsonScript) {
      const ld = {
        '@context': 'https://schema.org',
        '@type': 'WebApplication',
        name: appName,
        applicationCategory: 'BusinessApplication',
        description,
        operatingSystem: 'Web',
        url: href,
      };
      ldJsonScript.textContent = JSON.stringify(ld, null, 2);
    }
  }, [location.pathname, i18n.language]);

  return null;
}

export default PageTitle;
